import React, { useState } from "react";
import { Box } from "@chakra-ui/react";
import { edgePointTowards, centreOf } from "../boardGeometry";
import { STANDE } from "../../standeTokens";

const LINE = 2;
const HIT = 14;      // an invisible fat line, so a 2px stroke is clickable
const BADGE = 18;

/**
 * The lines between connected items.
 *
 * Endpoints are recomputed from the items on every render rather than stored,
 * so dragging a note drags its connectors with it and nothing can go stale.
 * A connector whose other end has been deleted simply stops rendering.
 */
const ConnectorLayer = ({ connectors, itemsById, readOnly, onRemove }) => {
  const [hovered, setHovered] = useState(null);

  const drawn = connectors
    .map((connector) => {
      const from = itemsById[connector.from];
      const to = itemsById[connector.to];
      if (!from || !to) return null;
      return {
        connector,
        a: edgePointTowards(from, centreOf(to)),
        b: edgePointTowards(to, centreOf(from)),
      };
    })
    .filter(Boolean);

  if (drawn.length === 0) return null;

  return (
    <Box position="absolute" inset="0" pointerEvents="none" zIndex={0}>
      <svg
        width="100%"
        height="100%"
        style={{ display: "block", overflow: "visible" }}
        aria-hidden={readOnly}
      >
        {drawn.map(({ connector, a, b }) => {
          const isHot = hovered === connector.id;
          return (
            <g key={connector.id}>
              <line
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={isHot ? STANDE.black : connector.colour || STANDE.black}
                strokeWidth={isHot ? LINE + 1 : LINE}
                strokeLinecap="round"
                opacity={isHot ? 1 : 0.55}
                style={{ transition: "opacity 0.12s, stroke-width 0.12s" }}
              />
              {!readOnly && (
                <line
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="transparent"
                  strokeWidth={HIT}
                  strokeLinecap="round"
                  style={{ pointerEvents: "stroke", cursor: "pointer" }}
                  onPointerEnter={() => setHovered(connector.id)}
                  onPointerLeave={() => setHovered(null)}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => onRemove(connector.id)}
                />
              )}
            </g>
          );
        })}
      </svg>

      {/* The cross sits outside the svg so it can be a real button with a
          tooltip and keyboard focus rather than svg shapes pretending. */}
      {!readOnly &&
        drawn.map(({ connector, a, b }) =>
          hovered === connector.id ? (
            <Box
              key={`badge-${connector.id}`}
              as="button"
              type="button"
              aria-label="Remove this connection"
              position="absolute"
              left={`${(a.x + b.x) / 2 - BADGE / 2}px`}
              top={`${(a.y + b.y) / 2 - BADGE / 2}px`}
              boxSize={`${BADGE}px`}
              borderRadius="full"
              bg={STANDE.black}
              color={STANDE.grey}
              fontSize="11px"
              lineHeight="1"
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="auto"
              cursor="pointer"
              boxShadow="0 2px 6px -2px rgba(0,0,0,0.45)"
              onPointerEnter={() => setHovered(connector.id)}
              onPointerLeave={() => setHovered(null)}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onRemove(connector.id)}
            >
              ×
            </Box>
          ) : null
        )}
    </Box>
  );
};

export default ConnectorLayer;
